import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import type {
  AccessibleProjectListResponse,
  AccessibleProjectSummary,
} from "@/lib/project-types";
import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, writeCache } from "@/lib/ui-cache";
import { accessibleProjectPreview } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";

const API_BASE = "/api/projects/accessible";
const PROJECTS_PAGE_SIZE = 50;

export interface UseAccessibleProjectsReturn {
  projects: AccessibleProjectSummary[];
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  /** Sets the server-side search term and restarts cursor pagination. */
  setSearchQuery: (query: string) => void;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

/**
 * Load the project catalog independently from the pinned-projects hook.
 *
 * Search and cursor state live here so the caller only needs to debounce its
 * input before calling ``setSearchQuery``.
 */
export function useAccessibleProjects(
  getToken: AccessTokenGetter,
): UseAccessibleProjectsReturn {
  const verifiedScope = useCacheScope();
  const previousScopeRef = useRef(verifiedScope);
  const [projects, setProjects] = useState<AccessibleProjectSummary[]>([]);
  const [searchQuery, setSearchQueryState] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const searchQueryRef = useRef(searchQuery);

  const fetchPage = useCallback(async (
    cursor: string | null,
    append: boolean,
  ): Promise<void> => {
    const requestId = ++requestIdRef.current;
    const requestedScope = cacheScope();
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: String(PROJECTS_PAGE_SIZE) });
      if (searchQuery) params.set("q", searchQuery);
      if (cursor) params.set("cursor", cursor);

      const response = await fetchWithAuth(
        `${API_BASE}?${params.toString()}`,
        { headers: {} },
        getToken,
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          clearCacheOnAuthFailure();
          void invalidateCache("projectLists");
          setProjects([]);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AccessibleProjectListResponse = await response.json();
      if (requestId !== requestIdRef.current || requestedScope !== cacheScope()) return;
      if (!searchQuery && !cursor) {
        void writeCache("projectLists", "accessible", "first", {
          projects: data.projects.map(accessibleProjectPreview).filter((row): row is AccessibleProjectSummary => !!row),
          has_more: data.has_more === true, next_cursor: data.next_cursor ?? null,
        });
      }

      setProjects((current) => {
        if (!append) return data.projects;
        const knownIds = new Set(current.map((project) => project.id));
        return [...current, ...data.projects.filter((project) => !knownIds.has(project.id))];
      });
      setHasMore(data.has_more ?? false);
      setNextCursor(data.next_cursor ?? null);
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : i18n.t("projects:catalog.loadFailed"));
      if (!append) {
        setProjects([]);
        setHasMore(false);
        setNextCursor(null);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [getToken, searchQuery]);

  const refresh = useCallback(() => fetchPage(null, false), [fetchPage]);

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (previousScopeRef.current === verifiedScope) return;
    previousScopeRef.current = verifiedScope;
    requestIdRef.current += 1;
    setProjects([]);
    if (!verifiedScope) return;
    void refresh();
    return () => { requestIdRef.current += 1; };
  }, [verifiedScope]);

  const setSearchQuery = useCallback((query: string) => {
    const normalized = query.trim();
    if (searchQueryRef.current === normalized) return;
    searchQueryRef.current = normalized;
    // Invalidate an in-flight page immediately so a slower previous search
    // cannot briefly overwrite this query's reset state.
    requestIdRef.current += 1;
    setSearchQueryState(normalized);
    setProjects([]);
    setHasMore(false);
    setNextCursor(null);
    setError(null);
    setIsLoading(true);
  }, []);

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoading || !hasMore || !nextCursor) return;
    await fetchPage(nextCursor, true);
  }, [fetchPage, hasMore, isLoading, nextCursor]);

  return {
    projects,
    hasMore,
    isLoading,
    error,
    setSearchQuery,
    refresh,
    loadMore,
  };
}
